/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import {useEffect, useRef} from 'react';
import {PLATFORM_IDS} from '@ion/core/src/utils/DeviceUtils';
import useDeviceFiles, {
  invalidatePreCachedDeviceFiles
} from './useDeviceFiles';

/**
 * Defines the return type for the `useLMUConfigDeviceFilePrefetch` hook which
 * includes a reference to the generated query cache key, as well as the current
 * loading state of the cache.
 */
export type UseLMUConfigDeviceFilePrefetchResult = {
  key: string[],
  isLoading: boolean
}

/**
 * The `useLMUConfigDeviceFilePrefetch` hook provides a prefetching mechanism for
 * loading LMU Config Device Files for a given account. Note, this hook is generally
 * used for pre-caching Device Files for LMU Configurations prior to their being
 * needed by subsequent components in order to help ensure immediate availability
 * when required.
 *
 * ```typescript
 *  import useLMUConfigDeviceFilePrefetch, {
 *   UseLMUConfigDeviceFilePrefetchResult
 * } from '@ion/query/src/hooks/useLMUConfigDeviceFilePrefetch';
 * ```
 *
 * @param   accountId The id of the account for which Device Files are to be loaded
 *          and cached for subsequent use.
 * @param   includeSubAccounts when `true`, indicates that the initial cache is to
 *          include device files for sub-accounts.
 * @returns the loading state of the pre-cache, which is `true` while being loaded,
 *          otherwise `false`. Clients
 */
export const useLMUConfigDeviceFilePrefetch = (
  accountId: string, includeSubAccounts: boolean, resetCache = false): UseLMUConfigDeviceFilePrefetchResult => {
  // provides a reference to prefetched caches for subsequent cache invalidation
  // when the account changes
  const prefetchedKeys = useRef<string[][]>([]);

  // loads device files for the current account and it's sub-accounts so as to
  // ensure the device files are available in the react query cache prior to
  // their being required ...
  const {key, isLoading} = useDeviceFiles({
    platformIds          : [`${PLATFORM_IDS.config}`],
    enabled              : !!accountId,
    enabledOnly          : true,
    suppressNullVersions : true,
    includeSubAccounts,
    accountId
  });

  // when a new key is generated, added it the the list of prefetched keys ...
  useEffect(() => {
    prefetchedKeys.current.push(key);
  }, [key]);

  // when resetCache is true, invalidate all previous caches based on the
  // currently cached keys, and reset the list of cached keys in preparation
  // for the next cache ...
  useEffect(() => {
    if (resetCache) {
      prefetchedKeys.current?.forEach(
        invalidatePreCachedDeviceFiles
      );
      prefetchedKeys.current = [];
    }
  }, [resetCache]);

  return {key, isLoading};
};

export default useLMUConfigDeviceFilePrefetch;
